import { fromJS } from 'immutable';
import { createSelector } from 'reselect';
import { DateTime } from 'luxon';
import { ListFifteenMinuteProduction } from '../../api/Performance';
import { unwrapGraphQlList } from '../../utilities/graphql';
import { aggregateFifteenMinuteDataToThirty, aggregateFifteenMinuteDataToHour } from '../../utilities/charts';

// ACTIONS TYPES
export const FETCH_HOURLY_PRODUCTION_SUCCESS = 'customer-portal/performance/FETCH_HOURLY_PRODUCTION_SUCCESS';
export const FETCH_HOURLY_PRODUCTION_FAILURE = 'customer-portal/performance/FETCH_HOURLY_PRODUCTION_FAILURE';
export const FETCHING_HOURLY_PRODUCTION = 'customer-portal/performance/FETCHING_HOURLY_PRODUCTION';

// ACTION CREATORS
export const fetchHourlyProductionSuccess = (productionFifteenMinute, productionThirtyMinute, productionHourly) => ({
  type: FETCH_HOURLY_PRODUCTION_SUCCESS,
  productionFifteenMinute,
  productionThirtyMinute,
  productionHourly,
});

export const fetchHourlyProductionFailure = (err, startDate, endDate) => ({
  type: FETCH_HOURLY_PRODUCTION_FAILURE,
  error: err,
  startDate,
  endDate,
});

export const fetchingHourlyProduction = () => ({
  type: FETCHING_HOURLY_PRODUCTION,
});

// THUNKS
export const fetchHourlyProduction = (prospectId, startDate, endDate) => async dispatch => {
  dispatch(fetchingHourlyProduction());
  try {
    const { data } = await ListFifteenMinuteProduction(prospectId, startDate, endDate);
    const requestArgs = {
      prospectId,
      startDate,
      endDate,
    };
    const hourlyProduction = { ...requestArgs, data: aggregateFifteenMinuteDataToHour(data.site) };
    const fifteenMinuteProduction = { ...requestArgs, data: unwrapGraphQlList(data.site, 'siteProductionFifteenMinute.edges') };
    const thirtyMinuteProduction = { ...requestArgs, data: aggregateFifteenMinuteDataToThirty(data.site) };
    dispatch(fetchHourlyProductionSuccess(fifteenMinuteProduction, thirtyMinuteProduction, hourlyProduction));
  } catch (error) {
    dispatch(fetchHourlyProductionFailure(error.message, startDate, endDate));
  }
};

export const shouldFetchHourlyProduction = (hourlyProductionState, prospectId, startDate, endDate) => {
  if (hourlyProductionState.get('loading')) {
    return false;
  }

  const readings = hourlyProductionState.get('hourlyReadings');
  const existing = readings.filter(
    reading =>
      reading.prospectId === prospectId.toString() &&
      DateTime.fromISO(reading.startDate).hasSame(DateTime.fromISO(startDate), 'minute') &&
      DateTime.fromISO(reading.endDate).hasSame(DateTime.fromISO(endDate), 'minute'),
  );

  return existing.size === 0;
};

export const fetchHourlyProductionIfNeeded = (prospectId, startDate, endDate) => (dispatch, getState) => {
  if (!shouldFetchHourlyProduction(getState().hourlyProduction, prospectId, startDate, endDate)) {
    return null;
  }
  return dispatch(fetchHourlyProduction(prospectId, startDate, endDate));
};

// SELECTORS
const hourlyProductionSelector = state => state.hourlyProduction;

// NOTE: Lots of duplicate logic that should be cleaned up
export const getHourlyProduction = (prospectId, startDate, endDate) =>
  createSelector([hourlyProductionSelector], hourlyProduction => {
    const allReadings = hourlyProduction.get('hourlyReadings');
    const readings = allReadings.filter(
      reading =>
        reading.prospectId === prospectId.toString() &&
        DateTime.fromISO(reading.startDate).hasSame(DateTime.fromISO(startDate), 'minute') &&
        DateTime.fromISO(reading.endDate).hasSame(DateTime.fromISO(endDate), 'minute'),
    );

    if (readings.size > 0) {
      return readings.first().data;
    }
    return null;
  });

export const getFifteenMinuteProduction = (prospectId, startDate, endDate) =>
  createSelector([hourlyProductionSelector], hourlyProduction => {
    const allReadings = hourlyProduction.get('fifteenMinuteReadings');
    const readings = allReadings.filter(
      reading =>
        reading.prospectId === prospectId.toString() &&
        DateTime.fromISO(reading.startDate).hasSame(DateTime.fromISO(startDate), 'minute') &&
        DateTime.fromISO(reading.endDate).hasSame(DateTime.fromISO(endDate), 'minute'),
    );

    if (readings.size > 0) {
      return readings.first().data;
    }
    return null;
  });

export const getThirtyMinuteProduction = (prospectId, startDate, endDate) =>
  createSelector([hourlyProductionSelector], hourlyProduction => {
    const allReadings = hourlyProduction.get('thirtyMinuteReadings');
    const readings = allReadings.filter(
      reading =>
        reading.prospectId === prospectId.toString() &&
        DateTime.fromISO(reading.startDate).hasSame(DateTime.fromISO(startDate), 'minute') &&
        DateTime.fromISO(reading.endDate).hasSame(DateTime.fromISO(endDate), 'minute'),
    );

    if (readings.size > 0) {
      return readings.first().data;
    }
    return null;
  });

export const getHourlyProductionLoading = createSelector(
  [hourlyProductionSelector],
  hourlyProduction => hourlyProduction.get('loading') || !hourlyProduction.get('loaded'),
);

// REDUCER
export const initialState = fromJS({
  loaded: false,
  loading: false,
  error: null,
  hourlyReadings: [],
  fifteenMinuteReadings: [],
  thirtyMinuteReadings: [],
});

export default (state = initialState, action) => {
  switch (action.type) {
    case FETCH_HOURLY_PRODUCTION_SUCCESS:
      return state
        .set('loaded', true)
        .set('loading', false)
        .updateIn(['hourlyReadings'], arr => arr.push(action.productionHourly))
        .updateIn(['fifteenMinuteReadings'], arr => arr.push(action.productionFifteenMinute))
        .updateIn(['thirtyMinuteReadings'], arr => arr.push(action.productionThirtyMinute));
    case FETCH_HOURLY_PRODUCTION_FAILURE:
      return state.set('loading', false).set('loaded', true).set('error', action.error);
    case FETCHING_HOURLY_PRODUCTION:
      return state.set('loading', true);
    default:
      return state;
  }
};
